// First, install the marker clusterer library:
// npm install @googlemaps/markerclusterer

'use client';

import React, { useEffect, useState, useContext, useLayoutEffect, useRef, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';
import { GoogleMap, useLoadScript, PolygonF } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import Layout from '../Layout';
import ReportBuilder from '../../utils/ReportBuilder';
import { ArrowLongLeftIcon, ArrowLongRightIcon } from '@heroicons/react/20/solid';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { BoltIcon, LightBulbIcon, MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { aubergeine, nightMode, retro } from '../../styles/mapStyles';

const center = { lat: 42.73116, lng: -73.68657 };
const MAX_RECORDS = 10000;
const MAX_MAP_MARKERS = 10000; // Optimized clustering handles up to 10K markers efficiently

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const customMarkerIcon = {
  path: "M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z",
  fillColor: "#FF0000",
  fillOpacity: 1,
  strokeWeight: 2,
  strokeColor: "#FFFFFF",
  scale: 0.6,
};

const activeMarkerIcon = {
  ...customMarkerIcon,
  fillColor: "#00FF00",
};

const polygonOptions = {
  fillColor: "#FF0000",
  fillOpacity: 0.35,
  strokeColor: "#FF0000",
  strokeOpacity: 0.8,
  strokeWeight: 2,
};

// Custom cluster renderer function - optimized for up to 10,000 markers
const createClusterRenderer = () => {
  if (!window.google || !window.google.maps) {
    console.warn('Google Maps not loaded yet');
    return null;
  }

  return {
    render: ({ count, position }) => {
      // Create custom cluster marker with your app's styling
      // Updated thresholds for handling up to 10,000 markers
      const color = count > 500 ? "#dc2626" : count > 100 ? "#ea580c" : count > 25 ? "#d97706" : "#059669";
      const size = count > 500 ? 70 : count > 100 ? 60 : count > 25 ? 50 : 40;
      
      return new window.google.maps.Marker({
        position,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.8,
          strokeWeight: 2,
          strokeColor: "#ffffff",
          scale: size / 2,
        },
        label: {
          text: String(count),
          color: "white",
          fontSize: count > 100 ? "14px" : "12px", // Larger font for big clusters
          fontWeight: "bold",
        },
        title: `${count} measurements`,
        zIndex: 1000,
      });
    },
  };
};

export default function Measurements() {
  const { user } = useContext(AuthContext);
  const [measurements, setMeasurements] = useState([]);
  const [mapMarkers, setMapMarkers] = useState([]); // Separate markers for map display
  const [loading, setLoading] = useState(false);
  const [selectedMeasurements, setSelectedMeasurements] = useState([]);
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [operators, setOperators] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(16);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [exportDropdown, setExportDropdown] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(false);
  const mapRef = useRef(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedDetailsMeasurement, setSelectedDetailsMeasurement] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [geofencePolygon, setGeofencePolygon] = useState(null);
  const [allGeofences, setAllGeofences] = useState([]); // Store all project geofences
  const [memberIds, setMemberIds] = useState([]);
  const [mapStyle, setMapStyle] = useState([]);

  // Marker clustering refs
  const markerClustererRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);

  const exportMenuRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const DEBOUNCE_MS = 800;

  useEffect(() => {
    const handleExportClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setExportDropdown(null);
      }
    };

    if (exportDropdown !== null) {
      document.addEventListener('mousedown', handleExportClickOutside);
    } else {
      document.removeEventListener('mousedown', handleExportClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleExportClickOutside);
    };
  }, [exportDropdown]);

  useEffect(() => {
    document.title = "Measurements | TransTech Connect";
    return () => {
      document.title = "TransTech Connect";
    };
  }, []);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: 'AIzaSyBBfpoVAWYZaujXG5t93mFIJKqk3cwHynw',
  });

  // Cleanup function for markers and clusterer
  const cleanupMarkers = useCallback(() => {
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
      markerClustererRef.current = null;
    }
    
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
  }, []);

  // Create and manage markers with clustering
  const createMarkersWithClustering = useCallback((measurementsData) => {
    if (!mapRef.current || !isLoaded || !measurementsData.length) return;
    
    // Check if Google Maps API is loaded
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps API not loaded yet');
      return;
    }

    // Clean up existing markers
    cleanupMarkers();

    // Create info window (reuse single instance)
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    // Create markers
    const markers = measurementsData
      .filter(measurement => measurement.latitude && measurement.longitude)
      .slice(0, MAX_MAP_MARKERS) // Limit for performance
      .map(measurement => {
        const marker = new window.google.maps.Marker({
          position: { lat: measurement.latitude, lng: measurement.longitude },
          icon: measurement.id === activeMarkerId ? activeMarkerIcon : customMarkerIcon,
          title: measurement.Description1 || 'Measurement',
          map: null, // Don't add to map yet, clusterer will handle this
        });

        // Add click listener for info window
        marker.addListener('click', () => {
          setActiveMarkerId(measurement.id);
          
          // Update marker icons
          markersRef.current.forEach(m => {
            const isActive = m.measurement?.id === measurement.id;
            m.setIcon(isActive ? activeMarkerIcon : customMarkerIcon);
          });

          // Create info window content
          const contentString = `
            <div class="p-3 bg-white rounded-lg shadow-md" style="min-width: 250px;">
              <div class="flex items-center justify-start mb-3">
                <h3 class="text-base font-bold text-gray-800">Measurement Details</h3>
              </div>
              <div class="space-y-2">
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Density:</span>
                  <span class="ml-1 text-gray-900">${measurement.Density?.toFixed(1) || 'N/A'}</span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Compaction:</span>
                  <span class="ml-1 text-gray-900">${measurement.Compaction?.toFixed(1) || 'N/A'}</span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Surface Temp:</span>
                  <span class="ml-1 text-gray-900">${measurement.SurfaceTemp?.toFixed(1) || 'N/A'}</span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Operator:</span>
                  <span class="ml-1 text-gray-900">${measurement.operator_name || 'N/A'}</span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Project:</span>
                  <span class="ml-1 text-gray-900">${measurement.project_display_name || 'N/A'}</span>
                </p>
                <p class="text-sm">
                  <span class="font-medium text-gray-700">Time:</span>
                  <span class="ml-1 text-gray-900">${new Date(measurement.LocalTimeOfMeasurement).toLocaleString()}</span>
                </p>
              </div>
              <button 
                onclick="window.openMeasurementProject('${measurement.id}')"
                class="w-full mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-md hover:bg-blue-700 transition-all duration-200"
              >
                Open Project
              </button>
            </div>
          `;

          infoWindowRef.current.setContent(contentString);
          infoWindowRef.current.open(mapRef.current, marker);
        });

        // Store measurement data on marker for reference
        marker.measurement = measurement;
        
        return marker;
      });

    // Store markers reference
    markersRef.current = markers;

    // Create marker clusterer
    if (markers.length > 0) {
      const clusterRenderer = createClusterRenderer();
      if (clusterRenderer) {
        markerClustererRef.current = new MarkerClusterer({
          map: mapRef.current,
          markers: markers,
          renderer: clusterRenderer,
          gridSize: 80, // Increased for better clustering with more markers
          maxZoom: 14, // Clusters break apart at zoom level 14 (was 15)
          minimumClusterSize: 3, // Require at least 3 markers to form cluster (was 2)
          averageCenter: true,
          zoomOnClick: true,
          imagePath: undefined, // Use custom renderer instead
        });
      }
    }
  }, [activeMarkerId, isLoaded]);

  // Global function for info window buttons
  useEffect(() => {
    window.openMeasurementProject = async (measurementId) => {
      const measurement = markersRef.current
        .find(marker => marker.measurement?.id === measurementId)?.measurement;
      
      if (measurement && measurement.project_id) {
        window.open(`https://transtechconnect.com/project/${measurement.project_id}`, '_blank');
      } else {
        // Fallback
        await handleOpenProject(measurementId);
      }
    };

    return () => {
      delete window.openMeasurementProject;
    };
  }, []);

  // Fetch organization member IDs
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user) return;

      try {
        let tempMemberIds = [user.id];
        
        const { data: orgMember, error: orgError } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (orgError && orgError.code !== 'PGRST116') {
          console.error('Error fetching organization membership:', orgError);
          return;
        }

        if (orgMember) {
          const { data: orgMembers, error: orgMembersError } = await supabase
            .from('organization_members')
            .select('user_id')
            .eq('organization_id', orgMember.organization_id);

          if (orgMembersError) {
            console.error('Error fetching organization members:', orgMembersError);
            return;
          }

          tempMemberIds = orgMembers.map((member) => member.user_id);
        }

        setMemberIds(tempMemberIds);
      } catch (err) {
        console.error('Unexpected error fetching initial data:', err);
      }
    };

    fetchInitialData();
  }, [user]);

  // Fetch map markers (separate from table data for performance)
  const fetchMapMarkers = useCallback(async () => {
    if (!user || memberIds.length === 0) return;

    try {
      let query = supabase
        .from('measurements_with_details')
        .select('id, latitude, longitude, Density, Compaction, SurfaceTemp, operator_name, project_display_name, project_id, LocalTimeOfMeasurement, Description1')
        .in('profile_id', memberIds)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .limit(MAX_MAP_MARKERS);

      // Apply same filters as table data
      if (selectedProject) {
        query = query.eq('project_display_name', selectedProject);
      }
      if (selectedOperator) {
        query = query.eq('operator_name', selectedOperator);
      }
      if (startDate) {
        query = query.gte('LocalTimeOfMeasurement', new Date(startDate).toISOString());
      }
      if (endDate) {
        query = query.lte('LocalTimeOfMeasurement', new Date(endDate).toISOString());
      }

      const { data, error } = await query.order('LocalTimeOfMeasurement', { ascending: false });

      if (error) throw error;

      setMapMarkers(data || []);
      
      // Add performance logging for large datasets
      if (data && data.length > 5000) {
        console.log(`Loading ${data.length} markers with clustering...`);
      }
      
      // Create markers with clustering
      createMarkersWithClustering(data || []);

    } catch (err) {
      console.error('Error fetching map markers:', err);
    }
  }, [user, memberIds, selectedProject, selectedOperator, startDate, endDate, createMarkersWithClustering]);

  // Fetch filter options using the new view
  const fetchProjects = async () => {
    if (!user || memberIds.length === 0) return;
    
    try {
      const { data, error } = await supabase
        .from('measurements_with_details')
        .select('project_display_name')
        .in('profile_id', memberIds)
        .not('project_display_name', 'is', null);

      if (error) throw error;

      const uniqueProjects = Array.from(
        new Set(data.map(item => item.project_display_name))
      ).sort((a, b) => {
        if (a === 'No Project') return 1;
        if (b === 'No Project') return -1;
        return a.localeCompare(b);
      });

      setProjects(uniqueProjects);
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchOperators = async () => {
    if (!user || memberIds.length === 0) return;
    
    try {
      const { data, error } = await supabase
        .from('measurements_with_details')
        .select('operator_name')
        .in('profile_id', memberIds)
        .neq('operator_name', 'Unknown')
        .not('operator_name', 'is', null);

      if (error) throw error;

      const uniqueOperators = Array.from(
        new Set(data.map(item => item.operator_name))
      ).sort();

      setOperators(uniqueOperators);
    } catch (err) {
      console.error('Error fetching operators:', err);
    }
  };

  // Fetch filter options after memberIds is set
  useEffect(() => {
    if (memberIds.length > 0) {
      fetchProjects();
      fetchOperators();
    }
  }, [memberIds]);

  // Fetch map markers when filters change
  useEffect(() => {
    if (memberIds.length > 0) {
      fetchMapMarkers();
    }
  }, [memberIds, selectedProject, selectedOperator, startDate, endDate, fetchMapMarkers]);

  // Fetch all geofences for organization projects
  const fetchAllGeofences = useCallback(async () => {
    if (!user || memberIds.length === 0) {
      setAllGeofences([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('measurements_with_details')
        .select('project_id, project_name, project_geofence')
        .in('profile_id', memberIds)
        .not('project_geofence', 'is', null)
        .not('project_id', 'is', null);

      if (error) throw error;

      // Get unique projects with geofences
      const uniqueGeofences = [];
      const seenProjects = new Set();

      data?.forEach(item => {
        if (!seenProjects.has(item.project_id) && item.project_geofence) {
          seenProjects.add(item.project_id);
          
          try {
            const geoJson = typeof item.project_geofence === 'string' 
              ? JSON.parse(item.project_geofence) 
              : item.project_geofence;

            if (geoJson && geoJson.coordinates && geoJson.coordinates.length > 0) {
              const coords = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
              
              // Generate different colors for different projects
              const colors = [
                { fill: "#3B82F6", stroke: "#1D4ED8" }, // Blue
                { fill: "#10B981", stroke: "#047857" }, // Green
                { fill: "#F59E0B", stroke: "#D97706" }, // Amber
                { fill: "#EF4444", stroke: "#DC2626" }, // Red
                { fill: "#8B5CF6", stroke: "#7C3AED" }, // Purple
                { fill: "#06B6D4", stroke: "#0891B2" }, // Cyan
                { fill: "#F97316", stroke: "#EA580C" }, // Orange
                { fill: "#EC4899", stroke: "#DB2777" }, // Pink
              ];
              
              const colorIndex = uniqueGeofences.length % colors.length;
              const projectColor = colors[colorIndex];

              uniqueGeofences.push({
                id: item.project_id,
                name: item.project_name,
                coordinates: coords,
                options: {
                  fillColor: projectColor.fill,
                  fillOpacity: 0.2,
                  strokeColor: projectColor.stroke,
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                }
              });
            }
          } catch (parseError) {
            console.warn('Error parsing geofence for project:', item.project_name, parseError);
          }
        }
      });

      setAllGeofences(uniqueGeofences);
    } catch (err) {
      console.error('Error fetching all geofences:', err);
      setAllGeofences([]);
    }
  }, [user, memberIds]);

  // Fetch all geofences when component loads
  useEffect(() => {
    fetchAllGeofences();
  }, [fetchAllGeofences]);

  // Fetch specific geofence when a project is selected (for highlighting)
  useEffect(() => {
    const fetchSpecificGeofence = async () => {
      if (!selectedProject || selectedProject === 'No Project' || memberIds.length === 0) {
        setGeofencePolygon(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('measurements_with_details')
          .select('project_geofence')
          .in('profile_id', memberIds)
          .eq('project_display_name', selectedProject)
          .not('project_geofence', 'is', null)
          .limit(1)
          .single();

        if (error || !data?.project_geofence) {
          setGeofencePolygon(null);
          return;
        }

        const geoJson = typeof data.project_geofence === 'string' 
          ? JSON.parse(data.project_geofence) 
          : data.project_geofence;

        if (geoJson && geoJson.coordinates && geoJson.coordinates.length > 0) {
          const coords = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
          setGeofencePolygon(coords);
        } else {
          setGeofencePolygon(null);
        }
      } catch (err) {
        console.error('Error fetching specific geofence:', err);
        setGeofencePolygon(null);
      }
    };

    fetchSpecificGeofence();
  }, [selectedProject, memberIds]);

  // Fetch table measurements (separate from map markers)
  const fetchMeasurements = async (page = 1) => {
    if (!user || memberIds.length === 0) return;

    setLoading(true);
    try {
      const from = (page - 1) * rowsPerPage;
      const to = from + rowsPerPage - 1;

      let query = supabase
        .from('measurements_with_details')
        .select('*', { count: 'exact' })
        .in('profile_id', memberIds)
        .limit(MAX_RECORDS);

      if (selectedProject) {
        query = query.eq('project_display_name', selectedProject);
      }
      if (selectedOperator) {
        query = query.eq('operator_name', selectedOperator);
      }
      if (startDate) {
        query = query.gte('LocalTimeOfMeasurement', new Date(startDate).toISOString());
      }
      if (endDate) {
        query = query.lte('LocalTimeOfMeasurement', new Date(endDate).toISOString());
      }

      const { data, count, error } = await query
        .range(from, to)
        .order('LocalTimeOfMeasurement', { ascending: sortOrder === 'asc' });

      if (error) throw error;

      setMeasurements(data || []);
      setTotalRecords(count || 0);

      // Set map center
      if (data && data.length > 0) {
        const firstMeasurement = data[0];
        if (firstMeasurement.latitude && firstMeasurement.longitude) {
          const newCenter = { 
            lat: firstMeasurement.latitude, 
            lng: firstMeasurement.longitude 
          };
          setMapCenter(newCenter);
          if (mapRef.current) {
            mapRef.current.panTo(newCenter);
            mapRef.current.setZoom(16);
          }
        }
      } else {
        setMapCenter(null);
      }

    } catch (err) {
      console.error('Error fetching measurements:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch on filter changes
  useEffect(() => {
    if (!user || memberIds.length === 0) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchMeasurements(1);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [user, memberIds, selectedProject, selectedOperator, startDate, endDate, sortOrder, rowsPerPage]);

  // Fetch on page change
  useEffect(() => {
    if (user && memberIds.length > 0) {
      fetchMeasurements(currentPage);
    }
  }, [currentPage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupMarkers();
    };
  }, [cleanupMarkers]);

  const handleOperatorChange = (e) => {
    setSelectedOperator(e.target.value);
  };

  const handleProjectChange = (e) => {
    setSelectedProject(e.target.value);
    setSelectedOperator('');
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
  };

  useLayoutEffect(() => {
    if (checkbox.current) {
      const isIndeterminate =
        selectedMeasurements.length > 0 && selectedMeasurements.length < measurements.length;
      setChecked(selectedMeasurements.length === measurements.length);
      setIndeterminate(isIndeterminate);
      checkbox.current.indeterminate = isIndeterminate;
    }
  }, [selectedMeasurements, measurements]);

  const toggleAll = () => {
    setSelectedMeasurements(checked || indeterminate ? [] : measurements);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  };

  const handleCenterMeasurement = (measurement) => {
    if (measurement.latitude && measurement.longitude) {
      if (mapRef.current) {
        mapRef.current.setZoom(16);
        mapRef.current.panTo({ lat: measurement.latitude, lng: measurement.longitude });
      }
      setMapZoom(16);
      setMapCenter({ lat: measurement.latitude, lng: measurement.longitude });
      setActiveMarkerId(measurement.id);
      
      // Update marker icons
      markersRef.current.forEach(marker => {
        const isActive = marker.measurement?.id === measurement.id;
        marker.setIcon(isActive ? activeMarkerIcon : customMarkerIcon);
      });
    }
  };

  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || data.length === 0) {
      console.error("No data available to export.");
      return;
    }

    const columns = {
      "first_name": "First Name",
      "last_name": "Last Name", 
      "operator_name": "Operator",
      "project_display_name": "Project",
      "Density": "Density",
      "Compaction": "Compaction",
      "SurfaceTemp": "Surface Temperature",
      "LocalTimeOfMeasurement": "Local Time",
      "GpsCoordinates": "GPS Coordinates",
      "latitude": "Latitude",
      "longitude": "Longitude",
      "Description1": "Description 1",
      "Description2": "Description 2",
      "Station": "Station"
    };

    const headers = Object.values(columns);
    const rows = data.map((row) =>
      Object.keys(columns).map((key) => {
        let value = row[key];
        if (typeof value === "string") {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value !== null && value !== undefined ? value : "";
      }).join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  async function handleOpenProject(measurementId) {
    try {
      const measurement = measurements.find(m => m.id === measurementId);
      if (measurement && measurement.project_id) {
        window.open(`https://transtechconnect.com/project/${measurement.project_id}`, '_blank');
        return;
      }

      const { data, error } = await supabase
        .from('project_mapping')
        .select('project_id')
        .eq('measurement_id', measurementId)
        .single();

      if (error || !data) {
        console.error('Error fetching project mapping:', error);
        alert('No project found for this measurement.');
        return;
      }

      window.open(`https://transtechconnect.com/project/${data.project_id}`, '_blank');
    } catch (err) {
      console.error('Unexpected error fetching project mapping:', err);
      alert('No project found for this measurement.');
    }
  }

  const handleStyleChange = (style) => {
    setMapStyle(style);
    if (mapRef.current) {
      mapRef.current.setOptions({ styles: style });
    }
  };

  const handleViewDetails = (measurement) => {
    setSelectedDetailsMeasurement(measurement);
    setShowDetailsModal(true);
    setActiveDropdown(null);
  };

  if (!isLoaded) return <Layout><div>Loading Map...</div></Layout>;

  const totalPages = Math.ceil(totalRecords / rowsPerPage);

  return (
    <Layout>
      <div className="mb-4 h-[60vh] border-2 rounded-lg">
        <div className="w-full h-full rounded-md overflow-hidden">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%', borderRadius: 'lg' }}
            center={mapCenter || center}
            zoom={mapZoom}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            options={{ styles: mapStyle, fullscreenControl: false }}
          >
            {/* Map controls */}
            <div className="absolute top-4 right-4 flex flex-col space-y-2">
              {/* Style buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleStyleChange([])}
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    mapStyle.length === 0 ? 'border-yellow-500 bg-gray-800' : 'border-gray-300 bg-gray-600'
                  } hover:bg-gray-700 transition-all`}
                  title="Default Mode"
                >
                  <SunIcon className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={() => handleStyleChange(nightMode)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    mapStyle === nightMode ? 'border-yellow-500 bg-gray-800' : 'border-gray-300 bg-gray-600'
                  } hover:bg-gray-700 transition-all`}
                  title="Night Mode"
                >
                  <MoonIcon className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={() => handleStyleChange(aubergeine)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    mapStyle === aubergeine ? 'border-yellow-500 bg-gray-800' : 'border-gray-300 bg-gray-600'
                  } hover:bg-gray-700 transition-all`}
                  title="Aubergeine Mode"
                >
                  <BoltIcon className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={() => handleStyleChange(retro)}
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                    mapStyle === retro ? 'border-yellow-500 bg-gray-800' : 'border-gray-300 bg-gray-600'
                  } hover:bg-gray-700 transition-all`}
                  title="Retro Mode"
                >
                  <LightBulbIcon className="w-6 h-6 text-white" />
                </button>
              </div>
              
              {/* Info display */}
              <div className="bg-white px-3 py-2 rounded-md shadow-md text-sm text-gray-700">
                <div>{mapMarkers.length.toLocaleString()} measurements</div>
                {allGeofences.length > 0 && (
                  <div className="text-xs text-blue-600 mt-1">
                    {allGeofences.length} project {allGeofences.length === 1 ? 'boundary' : 'boundaries'}
                  </div>
                )}
                {mapMarkers.length >= MAX_MAP_MARKERS && (
                  <div className="text-xs text-orange-600 mt-1">
                    Limited to {MAX_MAP_MARKERS.toLocaleString()}
                  </div>
                )}
              </div>
            </div>
            
            {/* All project geofences - always displayed */}
            {allGeofences.map((geofence) => (
              <PolygonF
                key={geofence.id}
                paths={geofence.coordinates}
                options={geofence.options}
              />
            ))}
            
            {/* Highlighted geofence for selected project */}
            {geofencePolygon && (
              <PolygonF
                paths={geofencePolygon}
                options={{
                  ...polygonOptions,
                  fillOpacity: 0.4, // More prominent for selected project
                  strokeWeight: 3,
                  zIndex: 1000, // Ensure it appears on top
                }}
              />
            )}
          </GoogleMap>
        </div>
      </div>

      {/* Performance warnings */}
      {totalRecords > 15000 && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded-md">
          <p className="text-sm">
            <strong>Note:</strong> Showing {totalRecords.toLocaleString()} records. 
            Map displays up to {MAX_MAP_MARKERS.toLocaleString()} markers with clustering for optimal performance.
          </p>
        </div>
      )}
      
      {mapMarkers.length >= MAX_MAP_MARKERS && (
        <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded-md">
          <p className="text-sm">
            <strong>Map Performance:</strong> Displaying {MAX_MAP_MARKERS.toLocaleString()} of {totalRecords.toLocaleString()} markers with smart clustering. 
            Use filters for focused viewing of specific areas.
          </p>
        </div>
      )}

      {/* Filter controls */}
      <div className="sm:flex sm:items-center justify-between mb-4">
        <div className="sm:flex sm:items-center sm:space-x-4">
          <div className="w-full sm:w-auto">
            <select
              id="sortOrder"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
            >
              <option value="desc">Newest to Oldest</option>
              <option value="asc">Oldest to Newest</option>
            </select>
          </div>

          <div className="w-full sm:w-auto">
            <select
              id="operatorFilter"
              value={selectedOperator}
              onChange={handleOperatorChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
            >
              <option value="">All Operators</option>
              {operators.map((operator, index) => (
                <option key={index} value={operator}>
                  {operator}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full sm:w-auto">
            <select
              id="projectFilter"
              value={selectedProject}
              onChange={handleProjectChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
            >
              <option value="">All Projects</option>
              {projects.map((proj, index) => (
                <option key={index} value={proj}>
                  {proj}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block w-36 rounded-l-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
              placeholder="Start Date"
            />
            <div className="flex items-center px-2 text-gray-500">/</div>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-36 rounded-r-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
              placeholder="End Date"
            />
          </div>
        </div>

        <div className="relative ml-auto">
          <button
            className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold shadow-sm ${
              selectedMeasurements.length > 0
                ? exportSuccess
                  ? "bg-green-600 text-white"
                  : "bg-yellow-500 text-white hover:bg-yellow-600"
                : "bg-gray-400 cursor-not-allowed text-white"
            }`}
            onClick={(e) => {
              if (selectedMeasurements.length === 0) return;
              e.stopPropagation();
              if (!exportSuccess) setExportDropdown(exportDropdown === "export" ? null : "export");
            }}
            disabled={selectedMeasurements.length === 0 || exportSuccess}
          >
            {exportSuccess ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="mr-2 h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12l2 2 4-4m6 8V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h14z"
                  />
                </svg>
                Successful
              </>
            ) : (
              <>
                Export
                <svg
                  className="ml-2 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 11-1.414 1.414L11 7.414V14a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </>
            )}
          </button>

          {exportDropdown === "export" && (
            <div
              id="export-dropdown"
              className="absolute right-0 mt-2 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-10"
              ref={exportMenuRef}
            >
              <button
                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  exportToCSV(selectedMeasurements, "selected_measurements.csv");
                  setExportDropdown(null);
                  setExportSuccess(true);
                  setTimeout(() => {
                    setExportSuccess(false);
                  }, 2000);
                  setSelectedMeasurements([]);
                }}
              >
                Export to CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table section */}
      <div className="flow-root overflow-visible">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="relative rounded-lg overflow-visible border">
              <table className="min-w-full table-fixed divide-y divide-gray-300 rounded-lg border overflow-hidden">
                <thead className="bg-gray-50 rounded-lg border">
                  <tr>
                    <th scope="col" className="relative px-7 sm:w-12 sm:px-6">
                      <input
                        type="checkbox"
                        className="absolute left-4 top-1/2 -mt-2 size-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-600"
                        ref={checkbox}
                        checked={checked && selectedMeasurements.length > 0}
                        onChange={toggleAll}
                      />
                    </th>
                    <th className="min-w-[6rem] py-3.5 pr-3 text-left text-sm font-semibold text-gray-900">
                      Project
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Operator
                    </th>
                    <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                      Density
                    </th>
                    <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                      Compaction
                    </th>
                    <th className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                      Surface Temp
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Local Time
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="py-6 text-center text-sm text-gray-600">
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-3 text-gray-400" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                          </svg>
                          Loading measurements...
                        </div>
                      </td>
                    </tr>
                  ) : measurements.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="py-6 text-center text-sm text-gray-600 font-medium">
                        <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        No measurements found. Try adjusting your filters.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {measurements.map((measurement) => (
                        <tr
                          key={measurement.id}
                          className={selectedMeasurements.includes(measurement) ? 'bg-gray-50' : undefined}
                        >
                          <td className="relative px-7 sm:w-12 sm:px-6">
                            {selectedMeasurements.includes(measurement) && (
                              <div className="absolute inset-y-0 left-0 w-0.5 bg-yellow-600" />
                            )}
                            <input
                              type="checkbox"
                              className="absolute left-4 top-1/2 -mt-2 size-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-600"
                              value={measurement.id}
                              checked={selectedMeasurements.includes(measurement)}
                              onChange={(e) =>
                                setSelectedMeasurements(
                                  e.target.checked
                                    ? [...selectedMeasurements, measurement]
                                    : selectedMeasurements.filter((m) => m !== measurement)
                                )
                              }
                            />
                          </td>
                          <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900">
                            {measurement.project_display_name || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                            {measurement.operator_name || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                            {measurement.Density?.toFixed(1) || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                            {measurement.Compaction?.toFixed(1) || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                            {measurement.SurfaceTemp?.toFixed(1) || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {new Date(measurement.LocalTimeOfMeasurement).toLocaleString()}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <div className="relative">
                              <button
                                className="rounded-full p-2 ml-2 text-gray-600 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                onClick={() => setActiveDropdown(activeDropdown === measurement.id ? null : measurement.id)}
                              >
                                <EllipsisVerticalIcon className="w-5 h-5" />
                              </button>
                              {activeDropdown === measurement.id && (
                                <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-full w-36 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                                  <button
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    onClick={() => {
                                      setActiveDropdown(null);
                                      handleCenterMeasurement(measurement);
                                    }}
                                  >
                                    View on Map
                                  </button>
                                  <button
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    onClick={() => handleViewDetails(measurement)}
                                  >
                                    View Details
                                  </button>
                                  <button
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    onClick={async () => {
                                      setActiveDropdown(null);
                                      await handleOpenProject(measurement.id);
                                    }}
                                  >
                                    Open Project
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between border-t border-gray-200 px-4 sm:px-0 my-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowLongLeftIcon className="h-5 w-5" />
          </button>
          <span className="text-sm text-gray-700">
            Page <span className="font-medium">{currentPage}</span> of{' '}
            <span className="font-medium">{totalPages}</span>
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowLongRightIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            className="block rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 text-sm"
          >
            <option value={25}>25 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
            <option value={500}>500 rows</option>
            <option value={1000}>1000 rows</option>
          </select>
          <span className="text-sm text-gray-700">{totalRecords.toLocaleString()} records</span>
        </div>
      </div>

      {/* Modals */}
      {showReportBuilder && (
        <ReportBuilder
          measurements={selectedMeasurements}
          onClose={() => setShowReportBuilder(false)}
        />
      )}

      {showDetailsModal && selectedDetailsMeasurement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
          <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center mb-6 pb-3 border-b border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-900">Measurement Details</h2>
              <button
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                onClick={() => setShowDetailsModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Project:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.project_display_name || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Operator:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.operator_name || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Density:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Density?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Compaction:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Compaction?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Surface Temp:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.SurfaceTemp?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Local Time:</span>
                  <span className="flex-1 text-gray-900 text-sm">{new Date(selectedDetailsMeasurement.LocalTimeOfMeasurement).toLocaleString()}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Station:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Station || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Description 1:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Description1 || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Description 2:</span>
                  <span className="flex-1 text-gray-900 text-sm">{selectedDetailsMeasurement.Description2 || 'N/A'}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Latitude:</span>
                  <span className="flex-1 text-gray-900 text-sm">
                    {selectedDetailsMeasurement.latitude?.toFixed(5) || 'N/A'}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="w-1/3 font-medium text-gray-700 text-sm">Longitude:</span>
                  <span className="flex-1 text-gray-900 text-sm">
                    {selectedDetailsMeasurement.longitude?.toFixed(5) || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <button
                className="px-5 py-2 bg-yellow-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200"
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}